import { Locale } from '../studioLocale';
import { entryText } from '../entryLocale';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../apiBase';
import ModelKeyControl from './ModelKeyControl';

type Props = {
  locale: Locale;
  open: boolean;
  busy?: boolean;
  onClose: () => void;
  onApply: (workspaceSeed: any, packet: any) => void;
};
const stages: Record<string, string> = {
  read_source: '分块阅读原著', combine_reading: '整理全书脉络', plan: '构思改编',
  initial_scene: '写作初稿', explore: '比较改编方向', trial_scene: '试写关键场面',
  revise_program: '调整人物与前后场', complete_scene: '写作完整剧本',
  critique: '审读剧本', review_plan: '整理修订意见', revise_scene: '修改相关场面',
};
const rememberedRun = () => {
  try { return sessionStorage.getItem('morphor-sceneweave-run') || ''; } catch { return ''; }
};

const NovelAdaptationModal: React.FC<Props> = ({ locale, open, busy, onClose, onApply }) => {
  const t = (text: string) => entryText(locale, text);
  const [outputLanguage, setOutputLanguage] = useState<Locale>(locale);
  const fileRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [source, setSource] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [requirements, setRequirements] = useState(t('改编为影视短剧。保留原著主要人物关系与重要选择，允许调整场面次序和表现方式。通过动作与对白呈现冲突，避免重复解释。'));
  const [sceneCount, setSceneCount] = useState(6);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [packet, setPacket] = useState<any>(null);
  const [seed, setSeed] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [runId, setRunId] = useState(rememberedRun);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    if (!open || !runId) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/generation-runs/${encodeURIComponent(runId)}`);
        if (disposed) return;
        const record = response.data;
        const event = [...(record.events || [])].reverse().find((item: any) => item.stage === 'sceneweave' || item.event_type === 'sceneweave' || item.payload?.stage);
        const stage = event?.payload?.stage;
        const state = event?.payload?.state;
        const detail = state === 'reuse' ? t('（已完成）') : state === 'start' ? '' : state || '';
        setProgress(stage ? `${t(stages[stage] || '改编进行中')} ${detail}` : ((locale === 'zh' && record.latest_event?.title) || t('改编正在进行，可以关闭窗口后继续查看。')));
        if (record.status === 'completed') {
          const value = record.result_payload;
          setResult(value);
          setSeed(value?.workspace_seed);
          setProgress(t('改编完成，可以查看试写、修订依据和完整剧本。'));
          setRunId('');
          sessionStorage.removeItem('morphor-sceneweave-run');
          return;
        }
        if (record.status === 'failed') {
          setError(t(record.error_message || record.error || '改编中断。已完成步骤已保留；重新提交相同原著可复用这些步骤。'));
          setRunId('');
          sessionStorage.removeItem('morphor-sceneweave-run');
          return;
        }
      } catch (reason: any) {
        if (!disposed) setError(t(String(reason?.response?.data?.error || t('暂时无法读取进度，稍后会继续查询。'))));
        if ([401, 404].includes(reason?.response?.status)) {
          if (!disposed) setRunId('');
          sessionStorage.removeItem('morphor-sceneweave-run');
          return;
        }
      }
      if (!disposed) timer = setTimeout(poll, 10000);
    };
    void poll();
    return () => { disposed = true; if (timer) clearTimeout(timer); };
  }, [open, runId, locale]);

  if (!open) return null;
  const running = Boolean(runId);
  const invalidate = () => { setPacket(null); setSeed(null); setResult(null); setError(''); };
  const analyze = async () => {
    setError(''); setLoading(true);
    try {
      const response = file
        ? await (() => {
            const form = new FormData(); form.append('file', file); form.append('title', title);
            return axios.post(`${API_BASE}/novel-adaptation/import`, form);
          })()
        : await axios.post(`${API_BASE}/novel-adaptation/analyze`, { title, text: source });
      setPacket(response.data?.packet); setSeed(response.data?.workspace_seed); setResult(null);
    } catch (reason: any) {
      setError(t(String(reason?.response?.data?.error || reason?.message || t('原著解析失败'))));
    } finally { setLoading(false); }
  };
  const generate = async () => {
    setError(''); setLoading(true); setResult(null);
    try {
      const response = await axios.post(`${API_BASE}/novel-adaptation/generate`, {
        packet, brief: { requirements, scene_count: sceneCount, medium: 'screenplay', output_language: outputLanguage },
      });
      setRunId(response.data.run_id); sessionStorage.setItem('morphor-sceneweave-run', response.data.run_id);
      setProgress(t('已提交改编，正在等待阅读原著。'));
    } catch (reason: any) {
      setError(t(String(reason?.response?.data?.error || reason?.message || t('无法开始改编'))));
    } finally { setLoading(false); }
  };
  const evidence = Array.isArray(packet?.evidence_spans) ? packet.evidence_spans : [];
  return (
    <div className="modal-backdrop" onClick={() => !loading && onClose()}>
      <div className="modal-card modal-card--novel" role="dialog" aria-modal="true" aria-labelledby="novel-heading" onClick={(event) => event.stopPropagation()}>
        <div className="modal-card__header">
          <div className="panel-title"><div>
            <div className="panel-card__eyebrow">{t("剧本改编 · 织梦")}</div>
            <h2 id="novel-heading">{t("读原著，试写场面，完成改编")}</h2>
          </div></div>
          <button className="modal-close" type="button" onClick={onClose} disabled={loading}>{t("关闭")}</button>
        </div>
        <p className="panel-card__copy">{t("全文按章节分块阅读，再比较两种场面写法，调整人物、前段准备和后续发展。所有写作与审读均使用 API。")}</p>
        <details><summary>{t("文本模型 API Key")}</summary><ModelKeyControl locale={locale} /></details>
        <label className="form-label" htmlFor="novel-title">{t("项目名（可选）")}</label>
        <input id="novel-title" className="app-input" value={title} disabled={running || loading} onChange={(event) => { setTitle(event.target.value); invalidate(); }} placeholder={t("例如：雨夜失踪案")} />
        <div className="novel-import-toggle">
          <button className="app-button app-button--ghost app-button--small" disabled={running || loading} type="button" onClick={() => fileRef.current?.click()}>{t("选择 TXT / Markdown / EPUB")}</button>
          <input ref={fileRef} hidden type="file" accept=".txt,.md,.markdown,.epub,text/plain,application/epub+zip" onChange={(event) => { setFile(event.target.files?.[0] || null); invalidate(); }} />
          {file && <><span>{file.name}</span><button type="button" disabled={running || loading} onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; invalidate(); }}>{t("改用粘贴")}</button></>}
        </div>
        {!file && <>
          <label className="form-label" htmlFor="novel-source">{t("或粘贴原著正文")}</label>
          <textarea id="novel-source" className="app-textarea" disabled={running || loading} value={source} onChange={(event) => { setSource(event.target.value); invalidate(); }} placeholder={t("可导入完整长篇原著。最多500万字符；超出时会提示分卷，不截断正文。")} />
        </>}
        <label className="form-label" htmlFor="novel-requirements">{t("改编要求")}</label>
        <textarea id="novel-requirements" className="app-textarea" value={requirements} disabled={running || loading} maxLength={10000} onChange={(event) => { setRequirements(event.target.value); setResult(null); }} />
        <label className="form-label" htmlFor="novel-output-language">{locale === 'en' ? 'Screenplay language' : '剧本语言'}</label>
        <select id="novel-output-language" className="app-input" value={outputLanguage} disabled={running || loading} onChange={(event) => { setOutputLanguage(event.target.value as Locale); setResult(null); }}><option value="zh">中文</option><option value="en">English</option></select>
        <label className="form-label" htmlFor="novel-scenes">{t("场面数量")}</label>
        <input id="novel-scenes" className="app-input" type="number" min={2} max={24} value={sceneCount} disabled={running || loading} onChange={(event) => { setSceneCount(Number(event.target.value)); setResult(null); }} />
        {error && <div className="form-error" role="alert">{error}</div>}
        {progress && <p role="status" aria-live="polite">{progress}</p>}
        {packet && <section className="novel-packet-preview">
          <strong>{locale === 'en' ? 'Source: ' : '原著：'}{packet.source_summary?.character_count?.toLocaleString()} {locale === 'en' ? 'characters, ' : '字符，'}{packet.source_summary?.chapter_count} {locale === 'en' ? 'chapters, ' : '章，'}{packet.source_summary?.chunk_count} {locale === 'en' ? 'reading chunks' : '个阅读分块'}</strong>
          <p>{t("预览仅展示开头几条定位；生成时逐块读取全文，覆盖范围可在完成后查看。")}</p>
          <div className="novel-evidence-list">{evidence.slice(0, 4).map((item: any) => <article key={item.id}><b>{item.label}</b><span>{item.start_char}–{item.end_char}</span><p>{item.excerpt}</p></article>)}</div>
        </section>}
        {result && <section className="novel-packet-preview">
          <strong>{locale === 'en' ? 'Read ' : '已读取 '}{result.sceneweave?.source_coverage?.read_chunks}/{result.sceneweave?.source_coverage?.total_chunks} {locale === 'en' ? 'source chunks' : '个原文分块'}</strong>
          {result.sceneweave?.trials?.map((trial: any) => <details key={trial.alternative}><summary>{locale === 'en' ? 'Scene trial ' : '场面试写 '}{trial.alternative + 1}</summary><p>{trial.edit?.benefit}</p><p>{trial.edit?.risk}</p>{trial.parts?.map((part: any) => <pre key={part.scene_id}>{part.text}</pre>)}</details>)}
          <details><summary>{t("人物与前后场的调整")}</summary>{result.sceneweave?.revision?.dependency_changes?.map((change: any, index: number) => <p key={index}>{change.before} → {change.after}：{change.reason}</p>)}</details>
          <details><summary>{t("模型审读意见")}</summary>{result.sceneweave?.critique?.issues?.map((issue: any, index: number) => <p key={index}>{issue.evidence}<br />{issue.suggestion}</p>)}{Boolean(result.sceneweave?.critique?.unverified_issues?.length) && <p>{t("部分意见未找到对应原句，已保留稿件，未据此自动修改。")}</p>}</details>
          <label className="form-label" htmlFor="novel-result">{t("完整改编剧本")}</label>
          <textarea id="novel-result" className="app-textarea" readOnly rows={14} value={result.script_text || ''} />
        </section>}
        <div className="modal-actions">
          <button className="app-button app-button--ghost" type="button" onClick={onClose} disabled={loading}>{running ? t('稍后继续查看') : t('取消')}</button>
          {!packet && !running && <button className="app-button app-button--strong" type="button" onClick={() => void analyze()} disabled={loading || busy || (!file && !source.trim())}>{loading ? t('解析中…') : t('读取原著并预览')}</button>}
          {packet && !running && !result && <button className="app-button app-button--strong" type="button" onClick={() => void generate()} disabled={loading || busy || !requirements.trim() || sceneCount < 2 || sceneCount > 24}>{loading ? t('提交中…') : t('用织梦生成改编剧本')}</button>}
          {result && <button className="app-button app-button--strong" type="button" onClick={() => seed && onApply(seed, packet || { title })} disabled={!seed || busy}>{t("写入工作台并编辑")}</button>}
        </div>
      </div>
    </div>
  );
};
export default NovelAdaptationModal;
